import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useNavigate } from "react-router-dom";
import { CreditCard, ClipboardList, Sparkles, CheckCircle2, FileJson, FileText, Map } from "lucide-react";

const HowItWorks = () => {
  const navigate = useNavigate();

  const howItWorksSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Get Your Gamification Strategy",
    "description": "Three simple steps to get your personalized AI-powered gamification strategy for your app.",
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Purchase Access",
        "text": "Secure one-time payment of $5 via Stripe. No subscriptions, no hidden fees."
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Complete 7-Question Assessment",
        "text": "Takes about 10 minutes. Tell us about your app type, target users, usage patterns, and goals."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Receive Your Strategy",
        "text": "AI-generated personalized recommendations delivered instantly with a complete implementation package."
      }
    ]
  };

  return (
    <>
      <SEO 
        title="How It Works"
        description="Get your personalized gamification strategy in 3 simple steps: purchase access, complete a quick assessment, and receive your AI-powered strategy instantly."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/how-it-works"
        keywords="gamification process, how gamification works, app strategy steps, AI assessment"
        jsonLd={howItWorksSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main>
          {/* Hero Section */}
          <section className="pt-32 pb-12 px-4" aria-labelledby="how-it-works-heading">
            <header className="max-w-4xl mx-auto text-center space-y-6">
              <h1 id="how-it-works-heading" className="text-5xl md:text-6xl font-bold">How It Works</h1>
              <p className="text-xl text-muted-foreground">
                Three simple steps to get your personalized gamification strategy
              </p>
            </header>
          </section>

      {/* 3-Step Process */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto space-y-12">
          {/* Step 1 */}
          <Card className="p-8 md:p-12 shadow-lg border-2 border-primary/20">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <CreditCard className="w-10 h-10 text-primary-foreground" />
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-primary">1</span>
                  <h3 className="text-2xl font-bold">Purchase Access</h3>
                </div>
                <p className="text-lg text-muted-foreground">
                  Secure one-time payment of $5 via Stripe. No subscriptions, no hidden fees.
                </p>
              </div>
            </div>
          </Card>

          {/* Step 2 */}
          <Card className="p-8 md:p-12 shadow-lg border-2 border-accent/20">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                  <ClipboardList className="w-10 h-10 text-primary-foreground" />
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-accent">2</span>
                  <h3 className="text-2xl font-bold">Complete 7-Question Assessment</h3>
                </div>
                <p className="text-lg text-muted-foreground">
                  Takes about 10 minutes. Tell us about your app type, target users, usage patterns, and goals.
                </p>
              </div>
            </div>
          </Card>

          {/* Step 3 */}
          <Card className="p-8 md:p-12 shadow-lg border-2 border-primary/20">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-primary-foreground" />
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-primary">3</span>
                  <h3 className="text-2xl font-bold">Receive Your Strategy</h3>
                </div>
                <p className="text-lg text-muted-foreground">
                  AI-generated personalized recommendations delivered instantly with a complete implementation package.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">What You'll Get</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-lg">Customized Gamification Strategy</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                Tailored specifically to your app type and user needs
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-lg">Engagement Tier Recommendation</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                Core, Moderate, or High tier based on your requirements
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <FileJson className="w-6 h-6 text-accent flex-shrink-0" />
                <h3 className="font-semibold text-lg">Technical Specifications</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                JSON file with detailed implementation specs
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-accent flex-shrink-0" />
                <h3 className="font-semibold text-lg">Design Guidelines</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                PDF with visual design recommendations
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <Map className="w-6 h-6 text-primary flex-shrink-0" />
                <h3 className="font-semibold text-lg">Implementation Roadmap</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                Step-by-step guide to bring your strategy to life
              </p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-accent flex-shrink-0" />
                <h3 className="font-semibold text-lg">Transition Guidelines</h3>
              </div>
              <p className="text-muted-foreground ml-9">
                When and how to level up your gamification
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-muted-foreground">
            Transform your app with a personalized gamification strategy today
          </p>
          <Button 
            size="lg"
            onClick={() => navigate("/pricing")}
            className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
          >
            Get Your Strategy Now - $5
            </Button>
          </div>
        </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HowItWorks;
